import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Send, Paperclip, Image as ImageIcon, Settings, Eye, MessageSquare, Loader2, Copy, FileText, FileDown, FileSpreadsheet } from "lucide-react";
import Sidebar from "../components/Sidebar";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MarkdownRespuesta = ({ contenido }) => (
  <div className="qaia-markdown text-sm">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: (p) => <h3 className="text-base font-bold mt-3 mb-1.5 first:mt-0" {...p} />,
        h2: (p) => <h3 className="text-base font-bold mt-3 mb-1.5 first:mt-0" {...p} />,
        h3: (p) => <h4 className="text-sm font-bold mt-2.5 mb-1 first:mt-0" {...p} />,
        h4: (p) => <h5 className="text-sm font-semibold mt-2 mb-1 first:mt-0" {...p} />,
        p: (p) => <p className="my-1.5 first:mt-0 last:mb-0 leading-relaxed" {...p} />,
        ul: (p) => <ul className="list-disc pl-5 my-1.5 space-y-1" {...p} />,
        ol: (p) => <ol className="list-decimal pl-5 my-1.5 space-y-1" {...p} />,
        li: (p) => <li className="leading-relaxed" {...p} />,
        strong: (p) => <strong className="font-bold" {...p} />,
        a: (p) => <a className="underline text-[#0C314C] dark:text-[#45E69A]" target="_blank" rel="noopener noreferrer" {...p} />,
        blockquote: (p) => <blockquote className="border-l-2 border-[#45E69A] pl-3 my-1.5 italic" {...p} />,
        hr: () => <hr className="my-2 border-border" />,
        code: ({ inline, ...p }) => inline
          ? <code className="bg-black/10 dark:bg-white/10 rounded px-1 py-0.5 text-xs font-mono" {...p} />
          : <code className="block bg-black/10 dark:bg-white/10 rounded-lg p-3 my-1.5 text-xs font-mono overflow-x-auto whitespace-pre" {...p} />,
        table: (p) => <div className="overflow-x-auto my-1.5"><table className="border-collapse w-full text-xs" {...p} /></div>,
        th: (p) => <th className="border border-border px-2 py-1 bg-black/5 dark:bg-white/5 font-semibold text-left" {...p} />,
        td: (p) => <td className="border border-border px-2 py-1" {...p} />,
      }}
    >
      {contenido}
    </ReactMarkdown>
  </div>
);

export default function QAiaPage() {
  const { getAuthHeaders } = useAuth();
  const [config, setConfig] = useState(null);
  const [convs, setConvs] = useState([]);
  const [convActiva, setConvActiva] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [modelo, setModelo] = useState("gpt-5.5");
  const [enviando, setEnviando] = useState(false);
  const [modoImagen, setModoImagen] = useState(false);
  const [verTodas, setVerTodas] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [cfgForm, setCfgForm] = useState({ proveedor: "emergent", proveedor_propio: "openai", api_key_propia: "" });
  const chatEndRef = useRef(null);
  const fileRef = useRef(null);
  const esAdmin = config?.es_admin === true;

  const fmtFecha = (iso) => iso ? new Date(iso).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  const copiarRespuesta = async (contenido) => {
    try {
      await navigator.clipboard.writeText(contenido);
      toast.success("Respuesta copiada al portapapeles");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = contenido;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Respuesta copiada al portapapeles");
      } catch {
        toast.error("No se pudo copiar");
      }
    }
  };

  const scrollAbajo = () => setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);

  const descargarRespuesta = async (contenido, formato) => {
    try {
      toast.info("Generando archivo…");
      const tituloConv = (convActiva?.titulo && convActiva.titulo !== "Nueva conversación")
        ? convActiva.titulo
        : (mensajes.find((x) => x.rol === "user")?.contenido || "Respuesta QAia").slice(0, 60);
      const res = await axios.post(`${API}/qaia/exportar`,
        { contenido, formato, titulo: tituloConv },
        { ...getAuthHeaders(), responseType: "blob" });
      const ext = formato === "word" ? "docx" : formato === "excel" ? "xlsx" : "pdf";
      const nombre = tituloConv.replace(/[^\w\sáéíóúñÁÉÍÓÚÑ-]/g, "").trim() || "QAia";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${nombre}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Archivo descargado");
    } catch {
      toast.error("No se pudo generar el archivo");
    }
  };

  const cargarConvs = useCallback(async (todas = false) => {
    const res = await axios.get(`${API}/qaia/conversaciones?todas=${todas}`, getAuthHeaders());
    setConvs(res.data);
  }, [getAuthHeaders]);

  useEffect(() => {
    axios.get(`${API}/qaia/config`, getAuthHeaders()).then((r) => {
      setConfig(r.data);
      setModelo(r.data.modelo_default || "gpt-5.5");
      setCfgForm((p) => ({ ...p, proveedor: r.data.proveedor || "emergent", proveedor_propio: r.data.proveedor_propio || "openai" }));
    }).catch(() => toast.error("Error cargando QAia"));
    cargarConvs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirConv = async (conv) => {
    setConvActiva(conv);
    const res = await axios.get(`${API}/qaia/conversaciones/${conv.id}/mensajes`, getAuthHeaders());
    setMensajes(res.data);
    scrollAbajo();
  };

  const nuevaConv = async () => {
    const res = await axios.post(`${API}/qaia/conversaciones`, { modelo }, getAuthHeaders());
    setConvs((p) => [res.data, ...p]);
    setConvActiva(res.data);
    setMensajes([]);
  };

  const borrarConv = async (e, conv) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la conversación "${conv.titulo}"?`)) return;
    await axios.delete(`${API}/qaia/conversaciones/${conv.id}`, getAuthHeaders());
    setConvs((p) => p.filter((c) => c.id !== conv.id));
    if (convActiva?.id === conv.id) { setConvActiva(null); setMensajes([]); }
  };

  const enviar = async () => {
    if (!texto.trim() || enviando) return;
    let conv = convActiva;
    if (!conv) {
      const res = await axios.post(`${API}/qaia/conversaciones`, { modelo }, getAuthHeaders());
      conv = res.data;
      setConvs((p) => [conv, ...p]);
      setConvActiva(conv);
    }
    const prompt = texto.trim();
    // El backend auto-titula la conversación con el primer mensaje: reflejarlo en vivo
    if ((conv?.titulo || "Nueva conversación") === "Nueva conversación") {
      const nuevoTitulo = prompt.slice(0, 60);
      setConvActiva((c) => (c ? { ...c, titulo: nuevoTitulo } : c));
      setConvs((p) => p.map((x) => (x.id === conv.id ? { ...x, titulo: nuevoTitulo } : x)));
    }
    setTexto("");
    setEnviando(true);
    if (modoImagen) {
      setMensajes((p) => [...p, { id: "u", rol: "user", contenido: `🖼️ Generar imagen: ${prompt}`, created_at: new Date().toISOString() }]);
      scrollAbajo();
      try {
        const res = await axios.post(`${API}/qaia/conversaciones/${conv.id}/imagen`, { prompt }, getAuthHeaders());
        setMensajes((p) => [...p, { id: "a", rol: "assistant", contenido: res.data.texto, imagen_b64: res.data.imagen_b64, created_at: new Date().toISOString() }]);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error generando la imagen");
      } finally {
        setEnviando(false);
        setModoImagen(false);
        cargarConvs(verTodas);
        scrollAbajo();
      }
      return;
    }
    setMensajes((p) => [...p, { id: "u", rol: "user", contenido: prompt, created_at: new Date().toISOString() }, { id: "stream", rol: "assistant", contenido: "", created_at: new Date().toISOString() }]);
    scrollAbajo();
    try {
      const resp = await fetch(`${API}/qaia/conversaciones/${conv.id}/mensaje`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders().headers },
        body: JSON.stringify({ texto: prompt, modelo }),
      });
      if (!resp.ok) throw new Error((await resp.json())?.detail || "Error");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", acumulado = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lineas = buffer.split("\n\n");
        buffer = lineas.pop();
        for (const l of lineas) {
          if (!l.startsWith("data: ")) continue;
          const payload = l.slice(6);
          if (payload === "[DONE]") continue;
          const ev = JSON.parse(payload);
          if (ev.error) { toast.error(ev.error); continue; }
          if (ev.delta) {
            acumulado += ev.delta;
            setMensajes((p) => p.map((m) => (m.id === "stream" ? { ...m, contenido: acumulado } : m)));
          }
        }
      }
    } catch (err) {
      toast.error(err.message || "Error al enviar el mensaje");
      setMensajes((p) => p.filter((m) => m.id !== "stream"));
    } finally {
      setEnviando(false);
      cargarConvs(verTodas);
      scrollAbajo();
    }
  };

  const adjuntar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let conv = convActiva;
    if (!conv) {
      const res = await axios.post(`${API}/qaia/conversaciones`, { modelo }, getAuthHeaders());
      conv = res.data;
      setConvs((p) => [conv, ...p]);
      setConvActiva(conv);
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/qaia/conversaciones/${conv.id}/documento`, fd, getAuthHeaders());
      toast.success(res.data.message);
      abrirConv(conv);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error adjuntando el documento");
    }
  };

  const guardarConfig = async () => {
    try {
      await axios.put(`${API}/qaia/config`, cfgForm, getAuthHeaders());
      toast.success("Configuración guardada");
      setShowConfig(false);
      const r = await axios.get(`${API}/qaia/config`, getAuthHeaders());
      setConfig(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-64 flex h-screen pt-14 md:pt-0 overflow-hidden" data-testid="qaia-page">
      {/* Sidebar conversaciones */}
      <aside className="w-72 shrink-0 border-r bg-[#0C314C] text-white flex flex-col">
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#45E69A]" />
            <h1 className="text-xl font-bold">QAia</h1>
            {esAdmin && (
              <Button variant="ghost" size="icon" className="ml-auto text-white hover:bg-white/10" onClick={() => setShowConfig(true)} data-testid="qaia-config-btn">
                <Settings className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Button onClick={nuevaConv} className="w-full bg-[#45E69A] text-[#0C314C] hover:bg-[#b8e04a] font-semibold" data-testid="qaia-nueva-conv">
            <Plus className="w-4 h-4 mr-2" /> Nueva conversación
          </Button>
          {esAdmin && (
            <Button variant="ghost" size="sm" className="w-full text-white/80 hover:bg-white/10 hover:text-white"
              onClick={() => { const t = !verTodas; setVerTodas(t); cargarConvs(t); }} data-testid="qaia-ver-todas">
              <Eye className="w-4 h-4 mr-2" /> {verTodas ? "Ver solo las mías" : "Ver todas (admin)"}
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
          {convs.map((c) => (
            <div key={c.id} onClick={() => abrirConv(c)}
              className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm ${convActiva?.id === c.id ? "bg-white/15" : "hover:bg-white/10"}`}
              data-testid={`qaia-conv-${c.id}`}>
              <MessageSquare className="w-4 h-4 shrink-0 text-white/60" />
              <div className="flex-1 min-w-0">
                <p className="truncate">{c.titulo}</p>
                <p className="text-[10px] text-white/50 truncate" data-testid={`qaia-conv-fecha-${c.id}`}>{fmtFecha(c.updated_at || c.created_at)}</p>
                {verTodas && <p className="text-[10px] text-[#45E69A] truncate">{c.user_name}</p>}
              </div>
              <Trash2 className="w-4 h-4 shrink-0 opacity-0 group-hover:opacity-70 hover:!opacity-100" onClick={(e) => borrarConv(e, c)} />
            </div>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="border-b px-6 py-3 flex items-center gap-3">
          <span className="font-semibold truncate">{convActiva?.titulo || "QAia — Asistente de IA de QAsesores"}</span>
          {convActiva?.created_at && (
            <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="qaia-conv-creada">
              Creada: {fmtFecha(convActiva.created_at)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Select value={modelo} onValueChange={setModelo}>
              <SelectTrigger className="w-[190px] h-8" data-testid="qaia-modelo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(config?.modelos || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          {mensajes.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-muted-foreground">
              <Sparkles className="w-12 h-12 text-[#45E69A]" />
              <p className="text-lg font-medium text-foreground">¿En qué puedo ayudarte hoy?</p>
              <p className="text-sm max-w-md">Escribe tu consulta, adjunta un documento con el clip, o activa el modo imagen para generar una. También puedo responder preguntas sobre los datos de la plataforma según tus permisos.</p>
            </div>
          )}
          {mensajes.map((m, i) => (
            <div key={m.id + i} className={`flex ${m.rol === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`group/msg max-w-[75%] rounded-2xl px-4 py-3 text-sm ${m.rol === "user" ? "bg-[#0C314C] text-white whitespace-pre-wrap" : "bg-muted"}`}>
                {m.rol === "assistant" && m.contenido
                  ? <MarkdownRespuesta contenido={m.contenido} />
                  : (m.contenido || (enviando && m.id === "stream" ? <Loader2 className="w-4 h-4 animate-spin" /> : ""))}
                {m.imagen_b64 && <img src={`data:image/png;base64,${m.imagen_b64}`} alt="Imagen generada" className="mt-2 rounded-lg max-w-full" />}
                {m.created_at && (
                  <p className={`mt-1 text-[10px] text-right ${m.rol === "user" ? "text-white/60" : "text-muted-foreground"}`}>{fmtFecha(m.created_at)}</p>
                )}
                {m.rol === "assistant" && m.contenido && !(enviando && m.id === "stream") && (
                  <div className="mt-2 flex justify-end items-center gap-3 flex-wrap">
                    <button onClick={() => copiarRespuesta(m.contenido)} title="Copiar respuesta"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover/msg:opacity-100 transition-opacity"
                      data-testid={`qaia-copiar-btn-${i}`}>
                      <Copy className="w-3.5 h-3.5" /> Copiar
                    </button>
                    <button onClick={() => descargarRespuesta(m.contenido, "word")} title="Descargar como Word"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover/msg:opacity-100 transition-opacity"
                      data-testid={`qaia-word-btn-${i}`}>
                      <FileText className="w-3.5 h-3.5" /> Word
                    </button>
                    <button onClick={() => descargarRespuesta(m.contenido, "pdf")} title="Descargar como PDF"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover/msg:opacity-100 transition-opacity"
                      data-testid={`qaia-pdf-btn-${i}`}>
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </button>
                    <button onClick={() => descargarRespuesta(m.contenido, "excel")} title="Descargar como Excel"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover/msg:opacity-100 transition-opacity"
                      data-testid={`qaia-excel-btn-${i}`}>
                      <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <footer className="border-t px-6 py-4 pr-20">
          {modoImagen && <Badge className="mb-2 bg-[#45E69A] text-[#0C314C]">Modo imagen activo: describe la imagen a generar</Badge>}
          <div className="flex items-center gap-2">
            <input type="file" ref={fileRef} className="hidden" accept=".pdf,.docx,.xlsx,.txt,.csv,.md" onChange={adjuntar} />
            <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} title="Adjuntar documento" data-testid="qaia-adjuntar-btn">
              <Paperclip className="w-4 h-4" />
            </Button>
            <Button variant={modoImagen ? "default" : "outline"} size="icon" onClick={() => setModoImagen(!modoImagen)} title="Generar imagen" data-testid="qaia-imagen-btn">
              <ImageIcon className="w-4 h-4" />
            </Button>
            <Input value={texto} onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && enviar()}
              placeholder={modoImagen ? "Describe la imagen que deseas generar..." : "Escribe tu mensaje..."}
              className="flex-1" disabled={enviando} data-testid="qaia-input" />
            <Button onClick={enviar} disabled={enviando || !texto.trim()} className="bg-[#0C314C] hover:bg-[#2a4d7a]" data-testid="qaia-enviar-btn">
              {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </footer>
      </main>

      {/* Config admin */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md" data-testid="qaia-config-dialog">
          <DialogHeader><DialogTitle>Configuración de QAia (Admin)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Proveedor de IA</Label>
              <Select value={cfgForm.proveedor} onValueChange={(v) => setCfgForm((p) => ({ ...p, proveedor: v }))}>
                <SelectTrigger data-testid="qaia-cfg-proveedor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="emergent">Clave Universal Emergent (GPT + Claude + Gemini)</SelectItem>
                  <SelectItem value="propia">Mi propia clave API</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cfgForm.proveedor === "propia" && (
              <>
                <div className="space-y-2">
                  <Label>Proveedor de la clave propia</Label>
                  <Select value={cfgForm.proveedor_propio} onValueChange={(v) => setCfgForm((p) => ({ ...p, proveedor_propio: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI (ChatGPT)</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Clave API {config?.api_key_configurada && <span className="text-xs text-green-600">(ya configurada)</span>}</Label>
                  <Input type="password" value={cfgForm.api_key_propia} onChange={(e) => setCfgForm((p) => ({ ...p, api_key_propia: e.target.value }))} placeholder="sk-..." data-testid="qaia-cfg-apikey" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={guardarConfig} data-testid="qaia-cfg-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
